import Image from 'next/image'
import { stegaClean } from '@sanity/client/stega'
import { urlFor } from '@/sanity/lib/image'
import type {
  PortableTextBlock,
  PortableTextComponents,
  PortableTextMarkComponentProps,
  PortableTextTypeComponentProps,
} from '@portabletext/react'
import { Lexend } from 'next/font/google'
import { Fence } from './Fence'
import { Language } from 'prism-react-renderer'
import { Video } from '../markdoc/Video'
import { Callout, CalloutProps } from './Callout'
import { GoogleSlides, Youtube } from '../markdoc/iFrame'
import { Footnote, ScrollBackToSuperscript, Superscript } from '../markdoc/Note'
import { Card, CardProps } from './Card'
import TableBlock from './Table'
import SanityTable from './SanityTable'
import { useEffect, useState } from 'react'
import TabGroup, { TabGroupProps } from './TabGroup'
import StepGroup, { StepGroupProps } from './StepGroup'
import MuxVideo from './MuxVideo'

type SanityImageBlock = {
  _type: 'image'
  asset: {
    _ref: string
    _type: 'reference'
  }
  alt?: string
  widthPercentage?: number
  externalImageUrl?: string
}

const lexend = Lexend({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
  display: 'swap',
})

export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces and other invisible characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^\w-]/g, '') // Remove all non-word characters (except hyphens)
    .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
}

type SanitySpan = {
  _type: 'span'
  _key: string
  text?: string
  marks?: string[]
}

const getTextFromBlock = (block: PortableTextBlock): string => {
  if (!block?.children) return ''

  return block.children
    .filter(
      (child): child is SanitySpan =>
        typeof child === 'object' && child !== null && child._type === 'span'
    )
    .map((span: SanitySpan) => span.text || '')
    .join('')
}

export type CodeWithMetaBlock = {
  _type: 'codeWithMeta'
  code: {
    _type: 'code'
    language: string
    code: string
  }
  title?: string
  isExpandable?: boolean
  maxVisibleLines?: number
  highlights?: string
}

type VideoBlock = {
  googleCloudKey: string
}

type MuxVideoBlock = {
  video?: {
    asset?: {
      playbackId?: string
      assetId?: string
      filename?: string
    }
  }
  title?: string
  caption?: string
  autoplay?: boolean
  loop?: boolean
  controls?: boolean
}

type iFrameProps = {
  src: string
  type: 'youtube' | 'google-slides'
}

interface MarkValue {
  _type: string
  id?: string | number
}

export interface CardGroupProps {
  columns?: 1 | 2 | 3 | 4
  cards: CardProps[]
}

export const portableTextComponents: PortableTextComponents = {
  types: {
    image: ({ value }: PortableTextTypeComponentProps<SanityImageBlock>) => {
      if (!value?.asset?._ref && !value?.externalImageUrl) {
        return null
      }

      const [isFullscreen, setIsFullscreen] = useState(false)
      // Use the widthPercentage value, default to 100% if not provided
      const widthPercentage = value.widthPercentage || 100

      const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen)
      }

      // Handle escape key press to exit fullscreen mode
      useEffect(() => {
        const handleEscape = (event: KeyboardEvent): void => {
          if (event.key === 'Escape') {
            setIsFullscreen(false)
          }
        }

        // Add event listener for keydown
        document.addEventListener('keydown', handleEscape)

        // Clean up the event listener when component unmounts or isFullscreen changes
        return () => {
          document.removeEventListener('keydown', handleEscape)
        }
      }, [isFullscreen])

      // Determine the image URL - prioritize external URL if available
      const imageUrl = value.externalImageUrl
        ? value.externalImageUrl
        : value.asset?._ref
        ? urlFor(value.asset._ref).url()
        : ''

      return (
        <div
          className={`not-prose flex items-center justify-center ${
            isFullscreen
              ? 'fixed inset-0 z-50 cursor-zoom-out bg-slate-900 bg-opacity-90 p-6 lg:p-12 xl:p-20'
              : 'cursor-zoom-in'
          }`}
          onClick={toggleFullscreen}
        >
          {imageUrl && (
            <Image
              className={`!my-0 transition-all duration-300 ease-in-out ${
                isFullscreen
                  ? 'max-h-full max-w-full object-contain'
                  : 'mx-auto'
              }`}
              style={!isFullscreen ? { width: `${widthPercentage}%` } : {}}
              src={imageUrl}
              width={2000}
              height={2000}
              alt={value.alt || ''}
              unoptimized={!!value.externalImageUrl} // Skip Next.js image optimization for external URLs
            />
          )}
        </div>
      )
    },
    videoBlock: ({ value }: { value: VideoBlock }): JSX.Element => {
      return (
        <Video
          googleCloudKey={stegaClean(value.googleCloudKey)}
          divClassName=""
        />
      )
    },
    muxVideoBlock: ({ value }: { value: MuxVideoBlock }): JSX.Element => {
      return (
        <MuxVideo
          video={value.video}
          title={value.title}
          caption={value.caption}
          autoplay={value.autoplay}
          loop={value.loop}
          controls={value.controls}
        />
      )
    },
    callout: ({ value }: { value: CalloutProps }) => {
      return <Callout type={value.type} content={value.content} />
    },
    iFrame: ({ value }: { value: iFrameProps }) => {
      if (value.type === 'youtube') {
        return <Youtube src={value.src} />
      } else if (value.type === 'google-slides') {
        return <GoogleSlides src={value.src} />
      }
      return null
    },
    cardGroup: ({ value }: { value: CardGroupProps }) => {
      const { columns = 1, cards = [] } = value
      return (
        <div
          className={`not-prose my-12 grid grid-cols-1 gap-6 ${
            columns === 1
              ? 'sm:grid-cols-1'
              : columns === 2
              ? 'sm:grid-cols-2'
              : columns === 3
              ? 'sm:grid-cols-3'
              : columns === 4
              ? 'sm:grid-cols-2 lg:grid-cols-4'
              : 'sm:grid-cols-2'
          }`}
        >
          {cards.map((card: CardProps, index: number) => {
            return (
              <Card
                key={index}
                title={card.title}
                description={card.description}
                href={card.href}
                hasIcon={!!(card.iconType && card.iconName)}
                iconType={card.iconType}
                iconName={card.iconName}
              />
            )
          })}
        </div>
      )
    },
    tableBlock: TableBlock,
    sanityTable: SanityTable,
    codeWithMeta: ({ value }: { value: CodeWithMetaBlock }): JSX.Element => {
      return (
        <Fence
          language={value.code?.language as Language}
          shouldWrap={true}
          className={{
            'bg-color': '!bg-[#0A0B0C]',
            container: '!m-0 !px-3 !py-3 !leading-tight',
            code: '!px -0 !leading-tight',
          }}
          isExpandable={value.isExpandable}
          maxVisibleLines={value.maxVisibleLines || 15}
          highlights={value.highlights}
        >
          {value.code?.code || ''}
        </Fence>
      )
    },
    tabGroup: ({ value }: { value: TabGroupProps }): JSX.Element => {
      return <TabGroup value={value} />
    },
    stepGroup: ({ value }: { value: StepGroupProps }): JSX.Element => {
      return <StepGroup value={value} />
    },
  },
  marks: {
    code: ({ children }) => (
      <span className="break-words rounded bg-slate-700 px-0.5">
        <code className="prose prose-code:!font-mono text-[14px] font-normal leading-[24.5px] text-sky-300">
          {children}
        </code>
      </span>
    ),
    superscript: ({ value }: PortableTextMarkComponentProps<MarkValue>) => {
      return value?.id ? <Superscript id={value.id} /> : null
    },
    footnote: ({ value }: PortableTextMarkComponentProps<MarkValue>) => {
      return value?.id ? <Footnote id={value.id} /> : null
    },
    scrollBack: ({ value }: PortableTextMarkComponentProps<MarkValue>) => {
      return value?.id ? <ScrollBackToSuperscript id={value.id} /> : null
    },
  },
  block: {
    p: ({ children }) => {
      return <p className="!my-0 pb-6 pt-12 text-base">{children}</p>
    },
    h2: ({ children, value }) => {
      const text = getTextFromBlock(value)
      return (
        <h2
          id={slugify(text)}
          className={`${lexend.className} !my-0 pb-6 pt-7 text-2xl leading-8`}
        >
          {children}
        </h2>
      )
    },
    h3: ({ children, value }) => {
      const text = getTextFromBlock(value)
      return (
        <h3 id={slugify(text)} className="mb-3 text-xl font-bold">
          {children}
        </h3>
      )
    },
    h4: ({ children, value }) => {
      const text = getTextFromBlock(value)
      return (
        <h4 id={slugify(text)} className="mb-3 text-lg font-bold">
          {children}
        </h4>
      )
    },
    h5: ({ children, value }) => {
      const text = getTextFromBlock(value)
      return (
        <h5 id={slugify(text)} className="mb-3 text-base font-bold">
          {children}
        </h5>
      )
    },
    h6: ({ children, value }) => {
      const text = getTextFromBlock(value)
      return (
        <h6 id={slugify(text)} className="mb-3 text-sm font-bold">
          {children}
        </h6>
      )
    },
  },
}
